import { i18n } from '@b2ag/locale'
import type * as DTO from './data-transfer-objects'
import * as Domain from './domain'

/**
 * Represents the data required by the presenter component responsible of a single stock quantity
 */
export type StockQuantity = {
  label: string
  isHighlighted: boolean
}

/**
 * Represent an item of {@link VariantStocksInfo.stocks}.
 */
export type StockInfo = {
  warehouseId: string
  warehouseName: string
  available: StockQuantity
}

/**
 * Represents the data required by the presenter component responsible of the whole variant stocks
 */
export type VariantStocksInfo = {
  id: Domain.VariantId
  name: string
  hasOffers: boolean
  stocks: StockInfo[]
  totals: {
    available: StockQuantity
  }
}

export function presentVariantStocksInfos(
  variants: Domain.StockVariant[],
  stocks: Record<Domain.VariantId, DTO.RealtimeStocksInfo>,
): VariantStocksInfo[] {
  return variants.map((variant) => presentVariantStocksInfo(variant, stocks[variant.id]))
}

export function presentVariantStocksInfo(
  variant: Domain.StockVariant,
  stocksInfo: DTO.RealtimeStocksInfo,
): VariantStocksInfo {
  return {
    id: variant.id,
    name: variant.name,
    hasOffers: variant.offers.length > 0,
    stocks: stocksInfo.stocks
      .map((stockInfo) => presentStockInfo(stockInfo, stocksInfo.unit))
      .sort((a, b) => a.warehouseName.toUpperCase().localeCompare(b.warehouseName.toUpperCase())),
    totals: {
      available: presentStockQuantity(stocksInfo.totals.available, stocksInfo.unit),
    },
  }
}

export function presentStockInfo(stockInfo: DTO.RealtimeStockInfo, unit: Domain.Unit): StockInfo {
  return {
    warehouseId: stockInfo.warehouseId,
    warehouseName: stockInfo.warehouseName,
    available: presentStockQuantity(stockInfo.available, unit),
  }
}

export function presentStockQuantity(value: Domain.StockQuantity, unit: Domain.Unit): StockQuantity {
  if (Domain.isDisclosed(value)) {
    const presentedValue = presentQuantityValue(value)
    const separator = Domain.isDisclosed(unit) ? ' ' : ''
    const presentedUnit = Domain.isDisclosed(unit) ? `${unit}` : ''

    return {
      label: `${presentedValue}${separator}${presentedUnit}`,
      isHighlighted: value < 0,
    }
  }

  return {
    label: i18n._('[context/realtime-stocks] Quantité inconnue'),
    isHighlighted: false,
  }
}

export function presentQuantityValue(value: NonNullable<Domain.StockQuantity>): string {
  if (value === 0) return i18n._('[context/realtime-stocks] Quantité nulle')

  return `${i18n.number(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}
